import { message as antdMessage } from 'antd';
import axios from 'axios';
import get from 'lodash/get';
import type { SagaIterator } from 'redux-saga';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';

import { MESSAGES } from 'clients/utils/parse-axios-error';
import { ENTERPRISE_NOT_FOUND_MESSAGE } from 'constants/enterprise';
import Address from 'types/models/address';
import Country, { CountryAbbreviation } from 'types/models/country';
import Enterprise from 'types/models/enterprise';
import User from 'types/models/user';
import parseUserPhones from 'utils/phone-number/parse-user-phones';
import { getRequest, patchRequest } from 'utils/redux-saga-requests';

import {
  editEnterprise,
  editEnterpriseFailure,
  editEnterpriseSuccess,
  loadEnterprise,
  loadEnterpriseFailure,
  loadEnterpriseSuccess,
  setEnterprise
} from './actions';
import ActionTypes from './constants';
import { selectCurrentEnterprise } from './selectors';
import { getCountryByAbbr } from '../countries/actions';
import { selectCountriesAsArray } from '../countries/selectors';

export const parserEnterpriseOwner = (enterpriseOwner: User): User => {
  return parseUserPhones(enterpriseOwner);
};

export function* requestEnterprise(
  action: ReturnType<typeof loadEnterprise>
): SagaIterator {
  const {
    payload: { enterpriseSubdomain }
  } = action;
  try {
    const response = yield call(
      getRequest,
      'enterprise?join=owner&join=billingAddress',
      {
        params: {
          s: {
            $or: [
              { id: { $eq: Number(enterpriseSubdomain) } },
              { subdomain: { $eq: enterpriseSubdomain } }
            ]
          }
        }
      }
    );
    const { data }: { data: Enterprise[] } = response;
    const matchedEnterprise: Enterprise | undefined = data.find(enterprise =>
      enterprise.subdomain === enterpriseSubdomain || enterprise.id === Number(enterpriseSubdomain)
    );
    if (matchedEnterprise) {
      const enterpriseCountryAbbr: CountryAbbreviation =
        matchedEnterprise.billingAddress?.country ?? 'AUS';
      const cachedCountries: Country[] | undefined = yield select(
        selectCountriesAsArray
      );
      if (
        !cachedCountries?.find((c) => c.abbreviation === enterpriseCountryAbbr)
      ) {
        yield put(getCountryByAbbr(enterpriseCountryAbbr));
      }
      const enterpriseDataParsed: Enterprise = {
        ...matchedEnterprise,
        owner: matchedEnterprise.owner ? parserEnterpriseOwner(matchedEnterprise.owner) : matchedEnterprise.owner
      };
      yield all([
        put(setEnterprise(enterpriseDataParsed)),
        put(loadEnterpriseSuccess(response))
      ]);
    } else {
      yield all([
        put(setEnterprise(null)),
        put(loadEnterpriseFailure(ENTERPRISE_NOT_FOUND_MESSAGE))
      ]);
    }
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    const message = error.message === MESSAGES.NETWORK_ERROR
      ? error.message
      : get(error, 'response.data.message', 'Sorry, something went wrong');
    yield put(loadEnterpriseFailure(message, error));
  }
}

export function* watchloadEnterpriseRequest() {
  yield takeLatest(ActionTypes.LOAD_ENTERPRISE_REQUEST, requestEnterprise);
}

export function* requestEditEnterprise(
  action: ReturnType<typeof editEnterprise>
): SagaIterator {
  const {
    payload: { values }
  } = action;

  const enterprise = yield select(selectCurrentEnterprise);
  const enterpriseId = enterprise ? enterprise?.id : values.id;
  try {
    const payload: Omit<Partial<Enterprise>, 'billingAddress'> & { billingAddress?: Partial<Address> } = { ...values };

    // Only include billingAddress if billingAddressId or billingAddress is provided
    if (values.billingAddressId || values.billingAddress) {
      payload.billingAddress = {
        id: values.billingAddressId,
        ...values.billingAddress
      };
    }

    const response = yield call(patchRequest, `enterprise/${enterpriseId}`, payload);
    const { data } = response;
    yield all([put(setEnterprise(data)), put(editEnterpriseSuccess(response))]);
    antdMessage.success('Enterprise settings updated');
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    const message = get(
      error,
      'response.data.message',
      'Sorry, something went wrong.'
    );
    yield put(editEnterpriseFailure(message, error));
  }
}

export function* watchEditEnterprisesRequest() {
  yield takeLatest(ActionTypes.EDIT_ENTERPRISE_REQUEST, requestEditEnterprise);
}

export default function* enterpriseSaga() {
  yield all([
    fork(watchloadEnterpriseRequest),
    fork(watchEditEnterprisesRequest)
  ]);
}
